/* eslint-disable no-unused-vars */
import {
  useEffect,
  useRef,
  useCallback,
  memo,
  useMemo,
  useLayoutEffect,
  useState,
} from "react";
import PropTypes from "prop-types";
import { buildCaptionDisplayBlocks } from "../utils/captionBlocks";

const BG_MAP = { black: "#000000", white: "#ffffff", "yellow-400": "#FFD600" };
const TEXT_MAP = {
  black: "#000000",
  white: "#ffffff",
  "yellow-400": "#FFD600",
};

const SYNC_DEBOUNCE_MS = 80;
const FADE_RECENT_COUNT = 2;

export function getCaptionStyles(backgroundColor, textColor) {
  const bg = BG_MAP[backgroundColor] ?? "#000000";
  const darkBg = backgroundColor === "black" || bg === "#000000";
  let text = TEXT_MAP[textColor] ?? "#ffffff";
  if (darkBg && (textColor === "black" || text === "#000000" || !textColor)) {
    text = "#ffffff";
  }
  return { backgroundColor: bg, color: text };
}

function getScrollEdgePadding(viewH) {
  return {
    top: Math.max(64, Math.round(viewH * 0.1)),
    bottom: Math.max(88, Math.round(viewH * 0.14)),
  };
}

function getBlockFadeClass(index, total) {
  if (total <= 1) return "caption-block--active";
  const age = total - 1 - index;
  if (age === 0) return "caption-block--active";
  if (age <= FADE_RECENT_COUNT - 1) return "caption-block--recent";
  return "caption-block--past";
}

const RealCaption = ({
  captions,
  showSetting,
  textColor,
  backgroundColor,
  fontSize,
  justReconnected,
}) => {
  const bg = backgroundColor ?? "black";
  const fg = textColor ?? "white";
  const size = fontSize ?? 16;
  const captionLangCode = "en";
  const captionStyles = getCaptionStyles(bg, fg);

  const captionsContainerRef = useRef(null);
  const captionFlowRef = useRef(null);
  const captionBlocksRef = useRef(null);
  const prevCaptionCountRef = useRef(0);
  const syncDebounceRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 390,
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const displayBlocks = useMemo(
    () =>
      buildCaptionDisplayBlocks(
        captions,
        captionLangCode,
        size,
        viewportWidth,
      ),
    [captions, captionLangCode, size, viewportWidth],
  );

  const syncCaptionLayout = useCallback((behavior = "auto") => {
    const scroll = captionsContainerRef.current;
    const flow = captionFlowRef.current;
    const blocks = captionBlocksRef.current;
    if (!scroll || !flow || !blocks) return;

    const viewH = scroll.clientHeight;
    const blocksH = blocks.offsetHeight;
    const edge = getScrollEdgePadding(viewH);
    const bottomPad = Math.max(edge.bottom, viewH * 0.38);
    const centeredPadTop = (viewH - blocksH - bottomPad) / 2;
    const padTop = Math.max(edge.top, centeredPadTop);

    flow.style.setProperty("--caption-pad-top", `${padTop}px`);
    flow.style.setProperty("--caption-pad-bottom", `${bottomPad}px`);
    scroll.style.setProperty("--caption-edge-top", `${edge.top}px`);
    scroll.style.setProperty("--caption-edge-bottom", `${edge.bottom}px`);
    flow.style.setProperty("--caption-edge-top", `${edge.top}px`);
    flow.style.setProperty("--caption-edge-bottom", `${edge.bottom}px`);

    const blocksBottom = blocks.offsetTop + blocksH;
    const readingAnchor = viewH * 0.44;
    const targetScroll = blocksBottom - readingAnchor;
    const maxScroll = scroll.scrollHeight - scroll.clientHeight;

    scroll.scrollTo({
      top: Math.min(Math.max(0, targetScroll), maxScroll),
      behavior,
    });
  }, []);

  const scheduleSync = useCallback(
    (behavior = "auto", immediate = false) => {
      if (syncDebounceRef.current) {
        clearTimeout(syncDebounceRef.current);
        syncDebounceRef.current = null;
      }

      if (immediate) {
        syncCaptionLayout(behavior);
        return;
      }

      syncDebounceRef.current = setTimeout(() => {
        syncDebounceRef.current = null;
        syncCaptionLayout(behavior);
      }, SYNC_DEBOUNCE_MS);
    },
    [syncCaptionLayout],
  );

  useLayoutEffect(() => {
    const scroll = captionsContainerRef.current;
    const flow = captionFlowRef.current;
    if (!scroll || !flow || displayBlocks.length === 0) return;

    const measure = () => scheduleSync("auto", true);
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(scroll);
    ro.observe(flow);
    return () => ro.disconnect();
  }, [displayBlocks, scheduleSync]);

  useEffect(() => {
    return () => {
      if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    const scrollContainer = captionsContainerRef.current;
    if (!scrollContainer || captions.length === 0) return;

    const prevCount = prevCaptionCountRef.current;
    const added = captions.length - prevCount;
    prevCaptionCountRef.current = captions.length;

    const isCatchingUp =
      justReconnected ||
      (prevCount === 0 && captions.length >= 3) ||
      added > 5;

    scheduleSync(isCatchingUp ? "auto" : "smooth", isCatchingUp);
  }, [captions, scheduleSync, justReconnected]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && captions.length > 0) {
        scheduleSync("auto", true);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [scheduleSync, captions.length]);

  const blockCount = displayBlocks.length;

  return (
    <section
      className="md:flex w-full primaryFont flex-1 flex flex-col min-h-0 relative"
      style={{ backgroundColor: captionStyles.backgroundColor }}
    >
      <div
        id="transcript"
        ref={captionsContainerRef}
        className="transcript-container w-full flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex justify-center"
      >
        <div
          ref={captionFlowRef}
          className="caption-flow w-full max-w-6xl min-h-full text-left px-5 sm:px-7 md:px-9"
          style={{
            color: captionStyles.color,
          }}
        >
          <div ref={captionBlocksRef} className="caption-blocks">
            {displayBlocks.map((block, index) => (
              <article
                key={`caption-${index}`}
                className={`caption-block ${getBlockFadeClass(index, blockCount)}`}
              >
                {block.meta ? (
                  <div className="caption-time-speaker">{block.meta}</div>
                ) : null}
                <p className="caption-block-text m-0 font-bold leading-[1.35]">
                  {block.text}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

RealCaption.propTypes = {
  captions: PropTypes.array.isRequired,
  showSetting: PropTypes.bool.isRequired,
  justReconnected: PropTypes.bool,
  textColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const MemoizeRealCaption = memo(RealCaption, (prevProps, nextProps) => {
  return (
    prevProps.captions.length === nextProps.captions.length &&
    prevProps.showSetting === nextProps.showSetting &&
    prevProps.justReconnected === nextProps.justReconnected &&
    prevProps.textColor === nextProps.textColor &&
    prevProps.backgroundColor === nextProps.backgroundColor &&
    prevProps.fontSize === nextProps.fontSize
  );
});

export default MemoizeRealCaption;
